import { env } from '@/env';
import { isLocalRun } from '@/lib/local-run';
import { initializeFaro, getWebInstrumentations, LogLevel, type Faro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { IGNORED_CLIENT_ERRORS } from '@restaurant/types';
import { REALTIME_STREAM_URL } from '@/lib/realtime-stream-path';

const FARO_URL = 'https://faro.internal.96digital.de/collect';

// Escape a base URL into a RegExp so propagateTraceHeaderCorsUrls matches every
// path under the api origin (the option takes patterns, not bare strings).
function originPattern(url: string): RegExp {
  return new RegExp(url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
}

let faro: Faro | undefined;

/** Boot Grafana Faro (frontend errors, web-vitals, RUM traces) for the
 *  dashboard. Tracing runs INSIDE Faro (not a separate OTEL provider) so every
 *  error and log carries the trace_id/span_id of the span that was active —
 *  that's what links a Faro error in Loki to its Tempo trace. The traceparent
 *  header is propagated on api calls only, so a dashboard action, the api, and
 *  the worker are one end-to-end trace (the api's CORS allowedHeaders include
 *  `traceparent`/`tracestate`, see apps/api/src/main.ts). Spans reach Tempo via
 *  the same Faro collector as the logs.
 *
 *  Only DEPLOYED builds ship to the collector, which allowlists origins by
 *  CORS — anything else just floods the console with "Failed to fetch". */
export function initFaro(environment: string, version: string): void {
  if (faro || typeof window === 'undefined') return;
  if (isLocalRun()) return;
  faro = initializeFaro({
    url: FARO_URL,
    app: { name: 'restaurant-dashboard', version, environment },
    // Drop per-asset resource-timing events (faro.performance.resource) — ~91% of
    // the frontend volume and never looked at. Navigation timing, web-vitals,
    // views, and errors are unaffected.
    trackResources: false,
    ignoreErrors: IGNORED_CLIENT_ERRORS,
    ignoreUrls: [REALTIME_STREAM_URL],
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        instrumentationOptions: {
          // Only inject traceparent on same-app api calls - never on third-party
          // hosts (MapBox, Firebase), which would reject the unexpected header.
          propagateTraceHeaderCorsUrls: env.apiUrl ? [originPattern(env.apiUrl)] : [],
        },
      }),
    ],
  });
}

export interface FaroUserContext {
  userId: string;
  systemRole?: string | null;
  franchiseId?: string | null;
  franchiseRole?: string | null;
  restaurantId?: string | null;
  /** Per-restaurant role, or null for franchise-wide access with no explicit role. */
  restaurantRole?: string | null;
}

/**
 * Attach the signed-in identity to every SUBSEQUENT Faro event (errors, logs,
 * traces). Without it an authorization failure in Loki says which endpoint
 * refused but not who was refused, and the roles are exactly what decides that:
 * a franchise-wide-only actor and an assigned MANAGER hit the same URL and only
 * one of them gets a 403.
 *
 * Ids and roles ONLY. Faro payloads land in the SHARED Loki, so no email, name,
 * or anything else that identifies a person — Faro's `email`/`fullName`/
 * `username` meta fields are deliberately left unset.
 */
export function setFaroUser(u: FaroUserContext): void {
  if (!faro) return;
  faro.api.setUser({
    id: u.userId,
    // Faro's native comma-separated roles field, so a Loki query can filter on
    // role without reaching into attributes.
    roles: [u.systemRole, u.franchiseRole, u.restaurantRole]
      .filter((r): r is string => !!r)
      .join(','),
    attributes: {
      ...(u.franchiseId ? { franchiseId: u.franchiseId } : {}),
      ...(u.restaurantId ? { restaurantId: u.restaurantId } : {}),
      // Spelled out rather than omitted: "has access to this restaurant with no
      // per-restaurant role" is the state that produces the surprising 403s, and
      // a missing attribute would read as "we didn't capture it".
      restaurantRole: u.restaurantRole ?? 'franchise-wide',
    },
  });
}

/** Detach the identity on sign-out so the next account's events aren't attributed
 *  to the previous one (Faro keeps the user meta for the whole page lifetime). */
export function clearFaroUser(): void {
  faro?.api.resetUser();
}

/**
 * Report an error caught by a React error boundary.
 *
 * Boundaries SWALLOW the error — it never reaches `window.onerror`, which is how
 * Faro auto-captures uncaught errors. Adding a boundary without this call would
 * trade a white screen we can still see in Grafana for a tidy fallback we
 * cannot: strictly worse. Every boundary must report before it renders.
 */
export function reportBoundaryError(err: unknown, componentStack?: string): void {
  if (!faro) return;
  faro.api.pushError(err instanceof Error ? err : new Error(String(err)), {
    type: 'RenderError',
    context: {
      source: 'error-boundary',
      ...(componentStack ? { componentStack } : {}),
    },
  });
}

/**
 * Note a stale-chunk preload failure (see lib/stale-chunk-reload.ts) as a LOG,
 * not an exception: it is expected fallout from a deploy — the tab asked for a
 * hashed asset the new build no longer serves — and the reload recovers it.
 * Logged so the frequency stays measurable in Loki; a spike means tabs are
 * outliving deploys badly enough to be worth fixing at the deploy layer.
 */
export function reportStaleChunk(message: string, reloading: boolean): void {
  if (!faro) return;
  faro.api.pushLog([`stale chunk (${reloading ? 'reloading' : 'not reloading'}): ${message}`], {
    level: LogLevel.INFO,
    context: { source: 'stale-chunk-reload', reloading: String(reloading) },
  });
}

let themeAccentFallbackReported = false;

export function reportThemeAccentFallback(value: string): void {
  if (!faro || themeAccentFallbackReported) return;
  themeAccentFallbackReported = true;
  faro.api.pushLog([`theme accent fallback used, --rt-accent read as "${value}"`], {
    level: LogLevel.WARN,
    context: { source: 'brand-colors', value },
  });
}

export interface ApiErrorReport {
  message: string;
  url?: string;
  status?: number;
  traceId?: string;
  /** The thrown value, for transport failures (status 0). */
  cause?: unknown;
}

type TransportReason = 'aborted' | 'timeout' | 'offline' | 'unreachable' | 'unknown';

const TRANSPORT_ERROR_REPEAT_WINDOW_MS = 60_000;

const lastTransportErrorAt = new Map<TransportReason, number>();

function isRepeatTransportError(reason: TransportReason): boolean {
  const last = lastTransportErrorAt.get(reason);
  return last !== undefined && Date.now() - last < TRANSPORT_ERROR_REPEAT_WINDOW_MS;
}

/** The browser collapses every transport failure into the same opaque
 *  `TypeError: Failed to fetch`, so the reason has to be reconstructed. */
function classifyTransportError(err: unknown): TransportReason {
  const name = err instanceof Error ? err.name : '';
  if (name === 'AbortError') return 'aborted';
  if (name === 'TimeoutError') return 'timeout';
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'offline';
  if (err instanceof TypeError) return 'unreachable';
  return 'unknown';
}

/**
 * Report a *caught* API failure (transport error or HTTP >= 400) to Faro. Faro
 * already auto-captures UNCAUGHT JS errors and unhandled rejections; the API
 * client swallows its errors (react-query handles them), so they'd never reach
 * Faro without this. Together the two give 100% frontend error capture in Faro —
 * there is no backend client-error endpoint. No-op until Faro has booted (local
 * dev / skipped hosts), so callers never need to guard.
 */
export function reportApiError(r: ApiErrorReport): void {
  if (!faro) return;
  const transport = r.status === 0;
  const reason = transport ? classifyTransportError(r.cause) : undefined;
  const lowSignal = reason === 'aborted' || reason === 'offline';
  const visibility =
    transport && typeof document !== 'undefined' ? document.visibilityState : undefined;
  const context = {
    source: 'api-client',
    ...(r.url ? { url: r.url } : {}),
    ...(r.status != null ? { status: String(r.status) } : {}),
    ...(r.traceId ? { traceId: r.traceId } : {}),
    ...(reason ? { reason } : {}),
    // Connectivity at throw time: separates a resumed-from-sleep tab from an outage.
    ...(transport && typeof navigator !== 'undefined' ? { online: String(navigator.onLine) } : {}),
    ...(visibility ? { visibility } : {}),
  };

  const repeat = reason !== undefined && isRepeatTransportError(reason);

  if (transport && (lowSignal || visibility === 'hidden' || repeat)) {
    faro.api.pushLog(
      [
        `api ${reason ?? 'transport error'}${visibility === 'hidden' ? ' while hidden' : ''}${repeat ? ' (repeat)' : ''}: ${r.message}`,
      ],
      {
        level: LogLevel.INFO,
        context: repeat ? { ...context, repeat: 'true' } : context,
      },
    );
    return;
  }

  if (reason) lastTransportErrorAt.set(reason, Date.now());

  faro.api.pushError(new Error(reason ? `${reason}: ${r.message}` : r.message), {
    type: 'ApiError',
    context,
  });
}
